import { useLatticeDeform } from "./LatticeDeformContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { UndoRedoControls } from "../reusable/UndoRedoControls";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import {
    Download,
    Grid3x3,
    RotateCcw,
    Shapes,
    Share2,
    Upload,
    Waves,
} from "lucide-react";

export function LatticeDeformControls() {
    const {
        svg,
        error,
        svgInputRef,
        handleSvgLoad,
        viewBoxAttr,
        cols,
        rows,
        resample,
        maxSegmentFraction,
        circleSegments,
        showLattice,
        setShowLattice,
        lockAxis,
        setLockAxis,
        setCols,
        setRows,
        setResample,
        setMaxSegmentFraction,
        setCircleSegments,
        resetLattice,
        exportSvgString,
        canExport,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
        undo,
        redo,
        canUndo,
        canRedo,
    } = useLatticeDeform();

    const handleInt =
        (setter: (v: number) => void, min = 1, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n)) return;
            setter(Math.max(min, Math.min(max, Math.round(n))));
        };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n)) return;
            setter(Math.max(min, Math.min(max, n)));
        };

    return (
        <div className="panel-controls lattice-deform-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Lattice Deform</h2>
                    <ToolDescriptionPopover dialogLabel="Lattice Deform tool description">
                        <p>
                            Import an SVG or a raster image (PNG, JPEG, WebP,
                            ...) and warp it interactively with a 2D lattice. Drag
                            a mesh point to move it; hold <strong>Shift</strong>{" "}
                            to drag the whole row, <strong>Alt</strong> for the
                            whole column. Double-click a point to reset it.
                        </p>
                        <p>
                            Resampling subdivides long path segments before the
                            warp so curves stay smooth through deformed cells.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="lattice-deform"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Source" icon={Upload}>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => svgInputRef.current?.click()}
                        >
                            Choose SVG or image...
                        </button>
                        <input
                            ref={svgInputRef}
                            type="file"
                            accept=".svg,image/svg+xml,image/png,image/jpeg,image/webp,image/gif,image/bmp,image/avif"
                            className="upload-input"
                            onChange={handleSvgLoad}
                        />
                    </div>
                    {svg && (
                        <p className="upload-info upload-info--tight-top">
                            <strong>{svg.name}</strong>
                            {viewBoxAttr && (
                                <>
                                    {" "}
                                    - viewBox: <code>{viewBoxAttr}</code>
                                </>
                            )}
                        </p>
                    )}
                    {!svg && (
                        <p className="upload-hint">
                            SVG: paths, polygons, circles, and rects are warped.
                            Large rasters are downscaled (max edge 4096px) for
                            performance.
                        </p>
                    )}
                    {error && <p className="upload-error">{error}</p>}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Lattice" icon={Grid3x3}>
                    <div className="field-row">
                        <label className="field-label">
                            Columns
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={32}
                                step={1}
                                value={cols}
                                onChange={handleInt(setCols, 1, 32)}
                                debounceMs={250}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Rows
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={32}
                                step={1}
                                value={rows}
                                onChange={handleInt(setRows, 1, 32)}
                                debounceMs={250}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={showLattice}
                            onChange={setShowLattice}
                            label="Show lattice"
                            title="Toggle the lattice grid and draggable nodes overlay."
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={lockAxis}
                            onChange={setLockAxis}
                            label="Lock to 90 deg axis"
                            title="Constrain each drag to horizontal or vertical movement only (picked by the dominant initial direction)."
                        />
                    </div>
                    <div className="field-row">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={resetLattice}
                        >
                            <RotateCcw size={14} aria-hidden style={{ marginRight: 6 }} />
                            Reset lattice
                        </button>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Path quality" icon={Waves}>
                    <div className="field-row">
                        <CheckboxField
                            checked={resample}
                            onChange={setResample}
                            label="Resample paths"
                            title="Subdivide long L/C/Q segments before warping so curves follow the lattice smoothly."
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Max segment length (% of viewBox diagonal)
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                max={20}
                                step={0.1}
                                value={Number((maxSegmentFraction * 100).toFixed(2))}
                                onChange={handleFloat(
                                    (v) => setMaxSegmentFraction(v / 100),
                                    0.1,
                                    20,
                                )}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Shape options" icon={Shapes}>
                    <div className="field-row">
                        <label className="field-label">
                            Circle / ellipse segments
                            <DebouncedInput
                                type="number"
                                min={6}
                                max={256}
                                step={1}
                                value={circleSegments}
                                onChange={handleInt(setCircleSegments, 6, 256)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Export" icon={Download}>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download SVG
                    </button>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport || !exportSvgString}
                        getSvg={() =>
                            exportSvgString
                                ? {
                                      name: "lattice-deform-export.svg",
                                      content: exportSvgString,
                                  }
                                : null
                        }
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["lattice-deform"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
